/**
 * Reading the backend's reason out of a join/create failure.
 *
 * `guest.py` and `teams.py` answer 422 with single-language literals, and they
 * are not consistent about which language ("Dieses Team ist voll." next to
 * "Run has not started yet."), so every check has to match both. Two screens
 * need the same reasons — JoinTeamStep and InviteTeamStep — and keeping a
 * private copy in each is how one of them ends up missing a case: on 2026-07-29
 * neither recognised "already in another team", so the screen fell back to
 * "Beitritt fehlgeschlagen. Bitte versuche es erneut." and a participant
 * retried 21 times against a condition that retrying could never clear (#494).
 *
 * Same rule the backend follows for shared router logic: when a second caller
 * needs the same domain rule, move it out rather than copy it.
 */

const norm = (detail: string | undefined) => (detail || '').toLowerCase();

/**
 * The caller already belongs to a different team in this run.
 *
 * Worth naming precisely, because both apparent ways out are traps: a guest who
 * leaves cannot re-enter the run (SOW 8.2.6, #345), and the coordinator's only
 * member-level tool — removing the membership — writes the same departure
 * record. Only the coordination can resolve it, and not by removing them.
 */
export const isAlreadyInTeam = (detail?: string) => {
  const d = norm(detail);
  return d.includes('bereits in einem anderen team') || d.includes('already in');
};

/** The target team has reached `max_members_per_team`. */
export const isTeamFull = (detail?: string) => {
  const d = norm(detail);
  return d.includes('voll') || d.includes('full');
};

/** The run is not accepting joins yet — still configured or merely scheduled. */
export const isRunNotStarted = (detail?: string) => {
  const d = norm(detail);
  return (
    d.includes('not started') ||
    d.includes('noch nicht') ||
    d.includes('active run') || // teams.py: "…only be created in active runs."
    d.includes('configured') ||
    d.includes('scheduled')
  );
};

/** The run is paused; joining resumes when the coordination resumes it. */
export const isRunPaused = (detail?: string) => {
  const d = norm(detail);
  return d.includes('paused') || d.includes('pausiert');
};

/** The run is over. */
export const isRunEnded = (detail?: string) => {
  const d = norm(detail);
  return d.includes('ended') || d.includes('beendet');
};

/** Every team allowed for this run already exists. */
export const isTeamLimitReached = (detail?: string) => {
  const d = norm(detail);
  return d.includes('maximum team limit') || d.includes('cannot create more teams');
};
